// src/pages/MyAnalyses.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCircleArrowLeft } from "react-icons/fa6";
import { FileText, Trash2, Eye, Calendar, Tag, Layers, Copy } from 'lucide-react';
import { useAuthCheck } from '../hooks/useAuthCheck';

function MyAnalyses() {
    useAuthCheck();

    const navigate = useNavigate();
    const [analyses, setAnalyses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
    }, []);

    useEffect(() => {
        fetchAnalyses();
    }, []);

    const fetchAnalyses = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            const response = await fetch('https://insightdraft-backend.onrender.com/api/analyses/saved', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (data.success) {
                const analysesList = data.analyses || [];

                // ✅ Fetch full data for each analysis (since list doesn't include data)
                const analysesWithFullData = await Promise.all(
                    analysesList.map(async (analysis) => {
                        try {
                            const fullResponse = await fetch(
                                `https://insightdraft-backend.onrender.com/api/analyses/saved/${analysis.id}`,
                                { headers: { 'Authorization': `Bearer ${token}` } }
                            );
                            const fullData = await fullResponse.json();

                            if (fullData.success && fullData.analysis) {
                                // Parse data if it's a string
                                let parsedData = fullData.analysis.data;
                                if (typeof parsedData === 'string') {
                                    try {
                                        parsedData = JSON.parse(parsedData);
                                    } catch (e) {
                                        console.error('Parse error:', e);
                                        parsedData = {};
                                    }
                                }

                                return {
                                    ...analysis,
                                    data: parsedData
                                };
                            }
                        } catch (e) {
                            console.error('Failed to fetch full analysis:', analysis.id, e);
                        }
                        return { ...analysis, data: {} };
                    })
                );

                setAnalyses(analysesWithFullData);
            } else {
                setError(data.error);
            }
        } catch (err) {
            console.error('Fetch error:', err);
            setError('Failed to load analyses');
        } finally {
            setLoading(false);
        }
    };

    const deleteAnalysis = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm('Delete this analysis?')) return;

        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`https://insightdraft-backend.onrender.com/api/analyses/saved/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (data.success) {
                setAnalyses(prev => prev.filter(a => a.id !== id));
            }
        } catch (err) {
            alert('Failed to delete');
        }
    };

    // src/pages/MyAnalyses.jsx
    // ✅ viewAnalysis function - smart navigation based on section count
    const viewAnalysis = async (id, e) => {
        if (e) e.stopPropagation();

        const token = localStorage.getItem('token');
        try {
            // Pehle analysis fetch karo to check single ya full
            const response = await fetch(`https://insightdraft-backend.onrender.com/api/analyses/saved/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (data.success) {
                const analysis = data.analysis;
                let analysisData = analysis.data;
                if (typeof analysisData === 'string') {
                    analysisData = JSON.parse(analysisData);
                }

                const dataKeys = Object.keys(analysisData || {});
                const isSingleSection = dataKeys.length === 1;
                const analysisType = analysis.type?.includes('existing') ? 'existing' : 'new';

                // Save to localStorage for CategoryPage
                localStorage.setItem('analysisType', analysisType);

                if (isSingleSection) {
                    // ✅ Single section - direct category page
                    const sectionKey = dataKeys[0];

                    if (analysis.type === 'existing_section' || analysisType === 'existing') {
                        localStorage.setItem('businessAnalysis', JSON.stringify(analysisData));
                        localStorage.setItem('businessFormData', JSON.stringify({ businessName: analysis.title }));
                        navigate(`/saved/${id}/category/${sectionKey}`);
                    } else {
                        localStorage.setItem('newBusinessAnalysis', JSON.stringify(analysisData));
                        localStorage.setItem('newBusinessFormData', JSON.stringify({ idea: analysis.title }));
                        navigate(`/saved/${id}/category-new/${sectionKey}`);
                    }
                } else {
                    // ✅ Full analysis - overview page
                    if (analysisType === 'existing') {
                        localStorage.setItem('businessAnalysis', JSON.stringify(analysisData));
                    } else {
                        localStorage.setItem('newBusinessAnalysis', JSON.stringify(analysisData));
                    }
                    navigate(`/saved/${id}`);
                }
            }
        } catch (err) {
            console.error('View error:', err);
            alert('Failed to load analysis');
        }
    };

    // ✅ Get section count
    const getSectionCount = (analysis) => {
        try {
            if (!analysis.data) return 0;

            let data = analysis.data;
            if (typeof data === 'string') {
                data = JSON.parse(data);
            }

            return Object.keys(data).length;
        } catch (error) {
            return 0;
        }
    };

    // ✅ Check if analysis is single section
    const isSingleSectionAnalysis = (analysis) => {
        try {
            if (!analysis.data) return false;

            let data = analysis.data;
            if (typeof data === 'string') {
                data = JSON.parse(data);
            }

            return Object.keys(data).length === 1;
        } catch {
            return false;
        }
    };

    // ✅ Check if analysis has all sections (12)
    const isFullAnalysis = (analysis) => {
        return getSectionCount(analysis) === 12;
    };

    // ✅ Get section name if single section
    const getSectionName = (analysis) => {
        try {
            let data = analysis.data;
            if (typeof data === 'string') {
                data = JSON.parse(data);
            }

            const keys = Object.keys(data);
            if (keys.length === 1) {
                const key = keys[0];
                return key.replace(/([A-Z])/g, ' $1').trim()
                    .replace(/^\w/, c => c.toUpperCase());
            }
            return null;
        } catch {
            return null;
        }
    };

    if (loading) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center"
                style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
                <div className="spinner-border text-primary" />
            </div>
        );
    }

    return (
        <div className="min-vh-100 py-5" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
            <div className="container" style={{ maxWidth: 1000 }}>
                <div className="d-flex align-items-center mb-4">
                    <button
                        className="btn btn-outline-primary me-3"
                        onClick={() => navigate('/')}
                        style={{ borderRadius: '12px', padding: '8px 16px' }}
                    >
                        <FaCircleArrowLeft size={18} />
                    </button>
                    <h2 className="fw-bold mb-0" style={{ color: '#0d53bd' }}>My Saved Analyses</h2>
                </div>

                {error && <div className="alert alert-danger">{error}</div>}

                {analyses.length === 0 ? (
                    <div className="text-center py-5">
                        <FileText size={48} className="text-muted mb-3" />
                        <h5>No saved analyses yet</h5>
                        <p className="text-muted">Generate an analysis and save it to see it here.</p>
                        <button className="btn btn-primary mt-3" onClick={() => navigate('/new')}>
                            Start New Analysis
                        </button>
                        <button className="btn btn-outline-primary mt-3 ms-2" onClick={() => navigate('/existing')}>
                            Analyze Existing Business
                        </button>
                    </div>
                ) : (
                    <div className="row g-3">
                        {analyses.map(analysis => {
                            const isSingle = isSingleSectionAnalysis(analysis);
                            const isFull = isFullAnalysis(analysis);
                            const sectionCount = getSectionCount(analysis);
                            const sectionName = isSingle ? getSectionName(analysis) : null;
                            const analysisType = analysis.type || '';

                            return (
                                <div
                                    key={analysis.id}
                                    className="col-12 col-md-6"
                                    onClick={(e) => viewAnalysis(analysis.id, e)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div
                                        className="card p-3 h-100"
                                        style={{
                                            borderRadius: '16px',
                                            transition: 'all 0.3s ease',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-4px)';
                                            e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                    <h6 className="fw-bold mb-0">{analysis.title}</h6>
                                                </div>

                                                {/* ✅ Single section: Section name dikhega, Full analysis: "All Sections" dikhega - same position, same style */}
                                                {isSingle && sectionName && (
                                                    <p className="text-muted small mb-2" style={{ fontSize: '12px' }}>
                                                        Section: {sectionName}
                                                    </p>
                                                )}

                                                {isFull && !isSingle && (
                                                    <p className="text-muted small mb-2" style={{ fontSize: '12px' }}>
                                                        Sections: All
                                                    </p>
                                                )}

                                                <div className="d-flex gap-3 mb-2">
                                                    <small className="text-muted">
                                                        <Tag size={14} className="me-1" />
                                                        {analysisType.includes('existing') ? 'Existing Business' : 'New Idea'}
                                                    </small>
                                                    <small className="text-muted">
                                                        <Calendar size={14} className="me-1" />
                                                        {new Date(analysis.created_at).toLocaleDateString('en-IN', {
                                                            day: 'numeric',
                                                            month: 'short',
                                                            year: 'numeric'
                                                        })}
                                                    </small>
                                                </div>

                                                <small className="text-muted">
                                                    <Copy size={14} className="me-1" />
                                                    {sectionCount === 0 ? 'Loading sections...' :
                                                        (isSingle
                                                            ? `Single section • ${sectionCount} section`
                                                            : `Full analysis • ${sectionCount} sections`)
                                                    }
                                                </small>
                                            </div>

                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={(e) => viewAnalysis(analysis.id, e)}
                                                    title="View Analysis"
                                                    style={{ borderRadius: '8px' }}
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                <button
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={(e) => deleteAnalysis(analysis.id, e)}
                                                    title="Delete"
                                                    style={{ borderRadius: '8px' }}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

export default MyAnalyses;